import React, { Suspense, ComponentType } from 'react';

const fallback = <div className="c-loader" />;

// Wrapper that adds Suspense fallback to each lazy component
function lazyWithFallback<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
	const LazyComponent = React.lazy(factory);
	const Wrapped = (props: Record<string, any>) => (
		<Suspense fallback={fallback}>
			<LazyComponent {...props} />
		</Suspense>
	);
	Wrapped.displayName = `Lazy(${(LazyComponent as any).displayName || 'Component'})`;
	return Wrapped;
}

///////// Layouts /////////
export const RootLayout = lazyWithFallback(() => import('@layouts/RootLayout'));

export const GenerateQuotation = lazyWithFallback(() => import('@containers/customer/QuotationGenerator'));

export const Login = lazyWithFallback(() => import('@containers/auth/login'));

export const Signup = lazyWithFallback(() => import('@containers/auth/signup'));

export const PasswordReset = lazyWithFallback(() => import('@containers/auth/password-reset'));

export const Home = lazyWithFallback(() => import('@containers/home'));
export const Dashboard = lazyWithFallback(() => import('@containers/dashboard'));

export const NotFound = lazyWithFallback(() => import('@containers/not-found'));

export const AllLeads = lazyWithFallback(() => import('@containers/leads/allLeads'));

export const LeadQuotation = lazyWithFallback(() => import('@containers/leads/leadQuotation'));

export const Inspirations = lazyWithFallback(() => import('@containers/inspirations/allInspirations'));

export const AllCategories = lazyWithFallback(() => import('@containers/products/allCategories'));
export const VariantsGrouping = lazyWithFallback(() => import('@containers/products/productTaxonomy'));
export const ParametricVariants = lazyWithFallback(() => import('@containers/products/parametricVariants'));
export const AllProducts = lazyWithFallback(() => import('@containers/products/allCategories/allProducts'));

export const AddProduct = lazyWithFallback(() => import('@forms/AddCategorySubcategory/AddProductDetails'));
export const UpdateProduct = lazyWithFallback(() => import('@forms/AddCategorySubcategory/AddProductDetails'));
export const ViewProduct = lazyWithFallback(() => import('@containers/products/allCategories/productDetail'));
export const AllWarranty = lazyWithFallback(() => import('@containers/products/allCategories/allWarranty'));
export const DefaultModels = lazyWithFallback(() => import('@containers/products/defaultModels'));
export const AllInspirations = lazyWithFallback(() => import('@containers/inspirations/allInspirations'));
export const ViewInspiration = lazyWithFallback(
	() => import('@containers/inspirations/allInspirations/inspirationDetail')
);
export const AddInspiration = lazyWithFallback(() => import('@containers/inspirations/allInspirations/addInspiration'));
export const AddLeads = lazyWithFallback(() => import('@containers/leads/addLeads'));
export const LeadDetails = lazyWithFallback(() => import('@containers/leads/leadDetails'));

export const AllProjects = lazyWithFallback(() => import('@containers/projects/allProjects'));

export const AllOrders = lazyWithFallback(() => import('@containers/projects/allOrders'));

export const OrderDetails = lazyWithFallback(() => import('@containers/projects/projectDetails'));

export const AddProjects = lazyWithFallback(() => import('@containers/projects/addProject'));
export const ProjectDetails = lazyWithFallback(() => import('@containers/projects/projectDetails'));

export const ProjectLedger = lazyWithFallback(() => import('@containers/projects/projectLedger'));

export const Holidays = lazyWithFallback(() => import('@containers/holidays'));

export const ProjectActivity = lazyWithFallback(() => import('@containers/projects/projectActivity'));

export const NewProject = lazyWithFallback(() => import('@containers/NewProject'));
export const Timeline = lazyWithFallback(() => import('@containers/Timeline'));

export const QuotationEstimate = lazyWithFallback(
	() => import('@containers/quotations/bulkQuotations/quotationEstimate')
);

export const CommercialsPayment = lazyWithFallback(() => import('@containers/Commercials/Payments'));

export const Profile = lazyWithFallback(() => import('@containers/profile'));
export const Organization = lazyWithFallback(() => import('@containers/organization'));
export const SchedulerStageMapping = lazyWithFallback(
	() => import('@containers/organization/schedulerMapping')
);
export const Approval = lazyWithFallback(() => import('@containers/approval'));
export const DelayApproval = lazyWithFallback(() => import('@containers/approval/DelayApproval'));
export const DocumentApproval = lazyWithFallback(() => import('@containers/approval/DocumentApproval'));
export const WarrantyApproval = lazyWithFallback(() => import('@containers/approval/WarrantyApproval'));
export const DescopeApproval = lazyWithFallback(() => import('@containers/approval/DescopeApproval'));
export const ProductItemApproval = lazyWithFallback(() => import('@containers/approval/ProductItemApproval'));
export const AllTickets = lazyWithFallback(() => import('@containers/tickets/allTickets'));
export const TicketChat = lazyWithFallback(() => import('@containers/tickets/ticketChat'));

export const BuilderCosting = lazyWithFallback(() => import('@containers/BuilderCosting'));

export const PromoCode = lazyWithFallback(() => import('@containers/promoCode'));
export const ProjectPromoApply = lazyWithFallback(() => import('@containers/promoCode/ProjectPromoApply'));

export const ElectricalDashboard = lazyWithFallback(() => import('@containers/electrical/Dashboard'));
export const ElectricalProjectDetails = lazyWithFallback(() => import('@containers/electrical/ProjectDetails'));
export const ElectricalSitePhotos = lazyWithFallback(() => import('@containers/electrical/SitePhotos'));
export const ElectricalProductDetails = lazyWithFallback(() => import('@containers/electrical/ProductDetails'));

export const TechnicianDashboard = lazyWithFallback(() => import('@containers/technician/Dashboard'));

export const Incentives = lazyWithFallback(() => import('@containers/incentives'));

export const Feedback = lazyWithFallback(() => import('@containers/feedback'));

export const EmailConfig = lazyWithFallback(() => import('@containers/emailConfig'));
